import { colors } from 'baseui/tokens';
import stylesColors from 'common/styles/styles.colors';
import { string2money } from 'common/utils/string';
import DrawerHeader from 'components/common/drawer/drawer-header';
import { DrawerBody } from 'components/common/drawer/styles';
import Text from 'components/elements/text';
import { format } from 'date-fns';
import { SeparatorHorizontal, SeparatorVertical } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { TotalContent, TotalItemContent } from './styles';
import SummaryAddOnsItem from './summary-add-ons-item';
import SummaryItem from './summary-item';
import { CruiseSaleFormType } from '../form-type';
import useRecalculate from '../hooks/use-recalculate';

interface Props {
  onClose: () => void;
}

function Total({ label, price }: { label: string; price: number }) {
  return (
    <TotalContent>
      <TotalItemContent>
        <Text variant="LabelMedium">{label}:</Text>
        <SeparatorHorizontal $margin={8} />
        <Text variant="HeadingXSmall">{`Rp ${string2money(price)}`}</Text>
      </TotalItemContent>
    </TotalContent>
  );
}

export default function SummaryDrawer(props: Props) {
  const { t } = useTranslation();
  const { onClose } = props;
  const { control } = useFormContext<CruiseSaleFormType>();
  const [cruiseSales] = useWatch({
    control,
    name: ['cruiseSales'],
  });

  const { discount, otherCost, total, subtotal } = useRecalculate();

  return (
    <>
      <DrawerHeader
        onClose={onClose}
        title={t('ticketSales:cruise_sale_details')}
      />
      <DrawerBody>
        <Text variant="HeadingXSmall">{`${cruiseSales[0].cruiseName || '-'} | ${
          cruiseSales[0].startedAt
            ? format(cruiseSales[0].startedAt, 'dd-MM-yyyy')
            : ''
        } - ${
          cruiseSales[0].endedAt
            ? format(cruiseSales[0].endedAt!, 'dd-MM-yyyy')
            : ''
        } | ${cruiseSales[0].route || '-'}`}</Text>
        <SeparatorVertical $margin={24} />
        {cruiseSales.map((cruiseSale, idx) => {
          const cruiseDetails = cruiseSale?.cruiseSaleDetails || [];
          const cruiseGuests = cruiseSale?.cruiseSaleGuests || [];
          const cruiseAddOns = cruiseSale?.cruiseSaleAddOns || [];
          return (
            <React.Fragment key={cruiseSale.id}>
              <Text
                variant="LabelMedium"
                $style={{ color: stylesColors.primaryDefault }}
              >
                {t('cruise:cabin_detail')} {idx + 1}
              </Text>
              <SeparatorVertical $margin={16} />
              {cruiseSale.isGroupedPrice ? (
                <>
                  <SummaryItem
                    data={cruiseDetails}
                    guests={cruiseGuests}
                    isGrouped
                  />
                  {cruiseAddOns.length > 0 ? (
                    <SeparatorVertical $margin={24} />
                  ) : (
                    <SeparatorVertical $margin={24}>
                      <div
                        style={{ height: 1, backgroundColor: colors.gray200 }}
                      />
                    </SeparatorVertical>
                  )}
                </>
              ) : (
                <>
                  {cruiseDetails.map((cruiseGuest, idx) => {
                    const guestAddOns = cruiseAddOns.filter(
                      (cruiseAddOn) =>
                        cruiseAddOn.guestId === cruiseGuest.guestId,
                    );

                    return (
                      <React.Fragment key={idx}>
                        <SummaryItem
                          data={[cruiseGuest]}
                          guests={cruiseGuests}
                        />
                        <SeparatorVertical $margin={12} />
                        {guestAddOns.length > 0 && (
                          <>
                            <Text
                              variant="ParagraphSmall"
                              $style={{ color: colors.gray500 }}
                            >
                              {t('ticketSales:add_ons')}
                            </Text>
                            {guestAddOns.map((guestAddOn, idx) => (
                              <SummaryAddOnsItem key={idx} data={guestAddOn} />
                            ))}
                          </>
                        )}
                        <SeparatorVertical $margin={24}>
                          <div
                            style={{
                              height: 1,
                              backgroundColor: colors.gray200,
                            }}
                          />
                        </SeparatorVertical>
                      </React.Fragment>
                    );
                  })}

                  {/* {cruiseDetails.map((cruiseGuest, idx) => (
                    <SummaryItem key={idx} data={[cruiseGuest]} />
                  ))} */}
                </>
              )}

              {!!cruiseSale.cruiseSaleAddOns.length &&
                cruiseSale.isGroupedPrice && (
                  <>
                    <Text
                      variant="ParagraphSmall"
                      $style={{ color: colors.gray500 }}
                    >
                      {t('ticketSales:add_ons')}
                    </Text>
                    {cruiseSale.cruiseSaleAddOns.map((addOns, idx) => (
                      <React.Fragment key={idx}>
                        <SummaryAddOnsItem data={addOns} isGrouped />
                      </React.Fragment>
                    ))}
                    <SeparatorVertical $margin={24}>
                      <div
                        style={{ height: 1, backgroundColor: colors.gray200 }}
                      />
                    </SeparatorVertical>
                  </>
                )}
            </React.Fragment>
          );
        })}

        <Total label={t('common:subtotal')} price={subtotal} />
        <Total label={t('common:discount')} price={discount} />
        <Total label={t('common:other_cost')} price={otherCost} />
        <Total label={t('common:total')} price={total} />
      </DrawerBody>
    </>
  );
}
